/**
 * Admin login screen — styled to the POC (poc/admin-login.html).
 *
 * "D.O.S.E ADMIN CONSOLE" lockup, "Welcome back" + subcopy, email/password,
 * Remember me + Forgot password, a "Sign in to Admin" button and a back-to-site
 * link, set on the POC's soft aurora background.
 *
 * The auth itself is REAL: `useAuth().login()` -> POST /admin/auth/login. Typed
 * ApiError messages surface inline; on success we redirect to `/` (or the
 * originally requested route). No demo-credential box.
 */
import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { ApiError } from '../lib/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';

interface LocationState {
  from?: { pathname: string };
}

export default function Login() {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const redirectTo =
    (location.state as LocationState | null)?.from?.pathname ?? '/';

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      await login({ email, password });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          err.code === 'UNAUTHENTICATED'
            ? 'Invalid email or password.'
            : err.message,
        );
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-aurora relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-10">
      <div className="relative z-10 w-full max-w-md rounded-3xl border border-white/60 bg-surface p-8 shadow-[0_25px_60px_rgba(0,0,0,0.08),0_1px_3px_rgba(0,0,0,0.04)] dark:border-surface-dark-border dark:bg-surface-dark-muted sm:p-10">
        {/* Brand lockup — "D.O.S.E ADMIN CONSOLE" */}
        <div className="mb-8 flex items-center gap-3">
          <span className="grid h-12 w-12 grid-cols-2 gap-1 rounded-xl bg-primary p-2">
            <span className="rounded-full bg-dopamine" />
            <span className="rounded-full bg-oxytocin" />
            <span className="rounded-full bg-serotonin" />
            <span className="rounded-full bg-endorphins" />
          </span>
          <div className="leading-tight">
            <p className="text-xl font-bold tracking-[0.06em] text-surface-dark dark:text-surface">
              D<span className="text-dopamine">.</span>O
              <span className="text-dopamine">.</span>S
              <span className="text-dopamine">.</span>E
            </p>
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-surface-dark/55 dark:text-surface/55">
              Admin Console
            </p>
          </div>
        </div>

        <h1 className="font-display text-[1.75rem] font-bold tracking-tight text-surface-dark dark:text-surface">
          Welcome back
        </h1>
        <p className="mb-8 mt-2 text-sm leading-relaxed text-surface-dark/60 dark:text-surface/60">
          Sign in to manage content, users, and the wellness platform.
        </p>

        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          {error && (
            <div
              role="alert"
              className="rounded-xl border-l-[3px] border-danger bg-danger/10 px-4 py-3 text-sm font-medium text-danger"
            >
              {error}
            </div>
          )}
          {notice && (
            <div
              role="status"
              className="rounded-xl border-l-[3px] border-primary bg-primary/10 px-4 py-3 text-sm font-medium text-primary"
            >
              {notice}
            </div>
          )}

          <Input
            label="Email"
            type="email"
            name="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="magda@yourdose.app"
          />
          <Input
            label="Password"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
          />

          <div className="flex items-center justify-between py-1">
            <label className="inline-flex cursor-pointer select-none items-center gap-2 text-sm text-surface-dark/70 dark:text-surface/70">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 accent-primary"
              />
              <span>Remember me</span>
            </label>
            <button
              type="button"
              onClick={() =>
                setNotice(
                  'If an account exists for that email, a reset link is on its way.',
                )
              }
              className="text-sm font-medium text-primary transition-colors hover:text-primary-700 hover:underline"
            >
              Forgot password?
            </button>
          </div>

          <Button
            type="submit"
            size="lg"
            loading={submitting}
            disabled={!email || !password}
            className="mt-1 w-full"
          >
            Sign in to Admin
          </Button>
        </form>

        <a
          href="https://yourdose.app"
          className="mt-6 block text-center text-sm font-medium text-surface-dark/60 transition-colors hover:text-primary dark:text-surface/60"
        >
          ← Back to site
        </a>
      </div>
    </div>
  );
}
